import { useState, useEffect } from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { posthog } from '../lib/posthog';
import { setSentryUser, clearSentryUser } from '../lib/sentry';
import { clearAuthMethod } from '../components/auth/emailUtils';

/**
 * Manages Supabase auth state and syncs identity to PostHog for analytics.
 *
 * Supports two auth methods: Google OAuth and magic link (passwordless email).
 * Session is initialized eagerly via getSession(), then kept in sync via
 * onAuthStateChange listener. PostHog identity is set on both initial load
 * and SIGNED_IN events to handle page refreshes and new logins respectively.
 * PostHog.reset() on sign-out ensures analytics don't bleed across users.
 * Provider memory is cleared on sign-out to prevent stale pre-fills.
 */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Identify user in PostHog and Sentry
      if (session?.user) {
        posthog.identify(session.user.id, {
          email: session.user.email,
        });
        setSentryUser({ id: session.user.id, email: session.user.email });
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Track auth events in PostHog and Sentry
      if (event === 'SIGNED_IN' && session?.user) {
        posthog.identify(session.user.id, {
          email: session.user.email,
        });
        const authMethod = session.user.app_metadata?.provider === 'google' ? 'google' : 'magic_link';
        posthog.capture('user_signed_in', { auth_method: authMethod });
        setSentryUser({ id: session.user.id, email: session.user.email });
      } else if (event === 'SIGNED_OUT') {
        posthog.reset();
        clearSentryUser();
        clearAuthMethod();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string): Promise<{ error: AuthError | null }> => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async (): Promise<{ error: AuthError | null }> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    return { error };
  };

  const signInWithMagicLink = async (email: string): Promise<{ error: AuthError | null }> => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return {
    user,
    session,
    loading,
    signIn,
    signInWithGoogle,
    signInWithMagicLink,
    signOut,
  };
}
